import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import ExcelJS from 'exceljs';
import { Plus, Edit2, Trash2, Package, AlertCircle, Upload, Link, X, Download, FileSpreadsheet } from 'lucide-react';
import API_URL from '../../config/api';
import { GestionarLotesModal, ProductosPorVencerPanel } from './LotesManager';
import { ProductOptionsLinker, saveProductOptions } from './OpcionesPlato';
import OpcionesManager from './OpcionesManager';
import CustomAlert from './shared/CustomAlert';

// ── Importación masiva: mapeo de encabezados del archivo a los campos
// internos del producto. Acepta encabezados en español (los de la
// plantilla) y variantes en inglés, sin importar mayúsculas/acentos.
const normalizeHeader = (h) =>
  h.toString().trim().toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/\s+/g, '_');

const IMPORT_HEADER_MAP = {
  nombre: 'name', name: 'name',
  categoria: 'category', category: 'category',
  precio: 'price', price: 'price',
  stock: 'stock', cantidad: 'stock',
  control_stock: 'use_stock', controla_stock: 'use_stock', controlar_inventario: 'use_stock', use_stock: 'use_stock',
  codigo_barras: 'barcode', codigo: 'barcode', barcode: 'barcode',
  descripcion: 'description', description: 'description',
  recargo_para_llevar: 'takeaway_surcharge', arancel_para_llevar: 'takeaway_surcharge', takeaway_surcharge: 'takeaway_surcharge'
};

const IMPORT_TRUE_VALUES = new Set(['si', 'sí', 'true', '1', 'yes', 'x']);
const IMPORT_MAX_ROWS = 5000;

// Tamaño de página del listado de Productos — mismo patrón que Caja
// (ver CajaVentas.js): nunca se carga el catálogo completo de una vez,
// se pide de a páginas y se agrega "Cargar más" bajo demanda.
const PRODUCTS_PAGE_SIZE = 50;

// Parser CSV mínimo (RFC4180-ish): soporta campos entre comillas con comas
// y comillas escapadas (""), suficiente para lo que genera nuestra propia
// plantilla o una hoja exportada desde Excel/Sheets.
function parseCSV(text) {
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    if (inQuotes) {
      if (char === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; } else { inQuotes = false; }
      } else {
        field += char;
      }
    } else if (char === '"') {
      inQuotes = true;
    } else if (char === ',') {
      row.push(field); field = '';
    } else if (char === '\n') {
      row.push(field); rows.push(row); row = []; field = '';
    } else if (char === '\r') {
      // ignorar, \n cierra la fila
    } else {
      field += char;
    }
  }
  if (field.length > 0 || row.length > 0) { row.push(field); rows.push(row); }
  return rows.filter((r) => r.some((cell) => cell.trim() !== ''));
}

function csvTextToRawObjects(text) {
  const table = parseCSV(text);
  if (table.length < 2) return [];
  const headers = table[0];
  return table.slice(1).map((cols) => {
    const obj = {};
    headers.forEach((h, idx) => { obj[h] = cols[idx] !== undefined ? cols[idx] : ''; });
    return obj;
  });
}

// Convierte filas "crudas" (objetos con las cabeceras tal cual venían en
// el archivo) a la forma normalizada que espera el backend de importación.
function rawObjectsToImportRows(rawObjects) {
  return rawObjects
    .filter((obj) => Object.values(obj).some((v) => v !== undefined && v !== null && String(v).trim() !== ''))
    .map((obj) => {
      const mapped = {};
      Object.entries(obj).forEach(([key, value]) => {
        const normKey = IMPORT_HEADER_MAP[normalizeHeader(key)];
        if (normKey && mapped[normKey] === undefined) mapped[normKey] = value;
      });
      return {
        name: mapped.name !== undefined ? String(mapped.name).trim() : '',
        category: mapped.category !== undefined ? String(mapped.category).trim() : '',
        price: mapped.price !== undefined ? mapped.price : '',
        stock: mapped.stock !== undefined ? mapped.stock : '',
        use_stock: mapped.use_stock !== undefined ? IMPORT_TRUE_VALUES.has(String(mapped.use_stock).trim().toLowerCase()) : false,
        barcode: mapped.barcode !== undefined ? String(mapped.barcode).trim() : '',
        description: mapped.description !== undefined ? String(mapped.description).trim() : '',
        takeaway_surcharge: mapped.takeaway_surcharge !== undefined ? mapped.takeaway_surcharge : ''
      };
    });
}

function downloadTextFile(filename, text) {
  const blob = new Blob([text], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const EMPTY_PRODUCT = {
  name: '',
  category: '',
  price: '',
  description: '',
  image_url: '',
  takeaway_surcharge: '',
  stock: 0,
  use_stock: false,
  barcode: '',
  track_batches: false
};

// Comprimir imagen a base64 con tamaño máximo
const compressImage = (file, maxWidth = 400, quality = 0.75) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new window.Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

function ProductosManager({ token, clientData }) {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [planLimits, setPlanLimits] = useState(null);
  const [customAlert, setCustomAlert] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [imageMode, setImageMode] = useState('upload'); // 'upload' | 'url'
  const [uploadLoading, setUploadLoading] = useState(false);
  const [newProduct, setNewProduct] = useState(EMPTY_PRODUCT);
  const [showLotesModal, setShowLotesModal] = useState(false);
  // ⭐ Opciones de plato (2026-08-09). `null` = el editor todavía no cargó los
  // grupos del producto; el Linker los siembra la primera vez.
  const [productOptionGroups, setProductOptionGroups] = useState(null);
  // Pestañas del módulo: la lista de productos de siempre, y el catálogo de
  // opciones. Van juntas porque configurar un plato con opciones exige saltar
  // entre las dos.
  const [inventarioTab, setInventarioTab] = useState('productos');
  const [editLoadingId, setEditLoadingId] = useState(null);
  const fileInputRef = useRef(null);

  // ── Paginación del listado (ver PRODUCTS_PAGE_SIZE) ──
  const [productsPage, setProductsPage] = useState(1);
  const [productsTotal, setProductsTotal] = useState(0);
  const [productsHasMore, setProductsHasMore] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const productsAbortRef = useRef(null);
  const [exportingProducts, setExportingProducts] = useState(false);

  // ── Importación masiva de productos (CSV/XLSX) ──
  const [showImportModal, setShowImportModal] = useState(false);
  const [importStep, setImportStep] = useState('select'); // 'select' | 'preview' | 'result'
  const [importRows, setImportRows] = useState([]);
  const [importPreview, setImportPreview] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importFileName, setImportFileName] = useState('');
  const importFileInputRef = useRef(null);

  // ── Filtros de la tabla (solo client-side, no afectan la carga de datos) ──
  const [categoryFilter, setCategoryFilter] = useState('');
  const [priceFilter, setPriceFilter] = useState('');
  const [stockFilter, setStockFilter] = useState('');

  const hasActiveFilters = !!(categoryFilter || priceFilter || stockFilter);

  const clearFilters = () => {
    setCategoryFilter('');
    setPriceFilter('');
    setStockFilter('');
  };

  // ⭐ La categoría y el texto de búsqueda ya se resuelven en el servidor
  // (loadProducts) — acá solo quedan precio/stock, que son filtros de
  // "vista rápida" y por eso se aplican solo sobre lo ya cargado en esta
  // página (ver nota junto a los filtros en el JSX).
  const filteredProducts = useMemo(() => {
    return products.filter((p) => {
      if (priceFilter) {
        const price = parseFloat(p.price) || 0;
        if (priceFilter === '20+') {
          if (!(price > 20)) return false;
        } else if (!(price <= parseFloat(priceFilter))) {
          return false;
        }
      }

      if (stockFilter) {
        if (!p.use_stock) return false; // "Sin control" no participa en rangos de stock
        const stock = Number(p.stock) || 0;
        if (stockFilter === 'lt10' && !(stock < 10)) return false;
        if (stockFilter === 'gt10' && !(stock > 10)) return false;
        if (stockFilter === 'gt20' && !(stock > 20)) return false;
      }

      return true;
    });
  }, [products, priceFilter, stockFilter]);

  // Debounce del buscador (280ms), mismo criterio que Caja: no dispara una
  // petición por cada tecla, espera a que el cajero/administrador pare de
  // escribir.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 280);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    loadCategories();
    loadPlanLimits();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Categoría y búsqueda SÍ son server-side: cualquier cambio reinicia el
  // listado desde la página 1 (equivalente a "nueva búsqueda").
  useEffect(() => {
    loadProducts({ reset: true });
  }, [categoryFilter, debouncedSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  const showAlert = (type, title, message) => setCustomAlert({ type, title, message });

  // ⭐ Exportar a XLSX (mismo patrón que "Reporte Excel" en Reportes, ver
  // ReportesUnificado.js): se genera 100% en el navegador con ExcelJS, sin
  // endpoint nuevo en el backend. A diferencia del listado en pantalla
  // (paginado de a PRODUCTS_PAGE_SIZE), el export trae TODO el catálogo que
  // coincida con el filtro de categoría/búsqueda activo, paginando contra
  // el mismo endpoint hasta agotar `hasMore` -- así el archivo nunca queda
  // incompleto solo porque el cajero no le dio "Cargar más" en pantalla.
  // Los filtros de Precio/Stock son solo client-side (ver comentario más
  // abajo) y no se aplican acá, igual que ya pasa con "Cargar más".
  const exportProductsToXLSX = async () => {
    if (exportingProducts) return;
    setExportingProducts(true);
    try {
      let allProducts = [];
      let page = 1;
      const limit = 200;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const params = { page, limit };
        if (categoryFilter) params.category = categoryFilter;
        if (debouncedSearch) params.search = debouncedSearch;
        const res = await axios.get(`${API_URL}/api/pos/products`, {
          headers: { Authorization: `Bearer ${token}` },
          params
        });
        const { rows, hasMore } = res.data;
        allProducts = allProducts.concat(rows);
        if (!hasMore || rows.length === 0 || page > 200) break;
        page += 1;
      }

      const workbook = new ExcelJS.Workbook();
      workbook.creator = 'Gestor SaaS POS';
      workbook.created = new Date();

      const sheet = workbook.addWorksheet('Productos', { views: [{ state: 'frozen', ySplit: 1 }] });
      sheet.columns = [
        { header: 'Nombre', key: 'name', width: 30 },
        { header: 'Categoría', key: 'category', width: 20 },
        { header: 'Precio', key: 'price', width: 12 },
        { header: 'Controla Stock', key: 'use_stock', width: 14 },
        { header: 'Stock', key: 'stock', width: 10 },
        { header: 'Código de Barras', key: 'barcode', width: 18 },
        { header: 'Recargo Para Llevar', key: 'takeaway_surcharge', width: 18 },
        { header: 'Descripción', key: 'description', width: 35 }
      ];
      const headerRow = sheet.getRow(1);
      headerRow.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1D4ED8' } };
        cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      });
      headerRow.height = 22;

      allProducts.forEach((p) => {
        const row = sheet.addRow({
          name: p.name,
          category: p.category || '',
          price: parseFloat(p.price) || 0,
          use_stock: p.use_stock ? 'Sí' : 'No',
          stock: p.use_stock ? Number(p.stock) : '',
          barcode: p.barcode || '',
          takeaway_surcharge: parseFloat(p.takeaway_surcharge) || 0,
          description: p.description || ''
        });
        row.getCell('price').numFmt = '"$"#,##0.00';
        row.getCell('takeaway_surcharge').numFmt = '"$"#,##0.00';
        row.eachCell((cell) => {
          cell.border = {
            top: { style: 'thin', color: { argb: 'FFE5E7EB' } },
            bottom: { style: 'thin', color: { argb: 'FFE5E7EB' } },
            left: { style: 'thin', color: { argb: 'FFE5E7EB' } },
            right: { style: 'thin', color: { argb: 'FFE5E7EB' } }
          };
        });
        if (p.use_stock && Number(p.stock) <= 5) {
          row.getCell('stock').font = { bold: true, color: { argb: 'FFB91C1C' } };
        } else if (row.number % 2 === 0) {
          row.eachCell((cell) => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF9FAFB' } };
          });
        }
      });

      if (allProducts.length === 0) {
        sheet.addRow({ name: 'No hay productos que coincidan con el filtro actual' });
      }

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `productos-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error al exportar productos:', err);
      showAlert('error', 'Error', 'No se pudo exportar el listado de productos. Intenta nuevamente.');
    } finally {
      setExportingProducts(false);
    }
  };

  // ⭐ Ya no se precarga el catálogo completo (podían ser miles de filas).
  // Se pide de a páginas de PRODUCTS_PAGE_SIZE, con búsqueda/categoría
  // resueltas en el servidor (mismo endpoint paginado que ya usa Caja).
  // Cancela cualquier petición de página anterior en curso si el usuario
  // sigue escribiendo o cambia de categoría antes de que responda.
  const loadProducts = async ({ reset = false } = {}) => {
    if (productsAbortRef.current) productsAbortRef.current.abort();
    const controller = new AbortController();
    productsAbortRef.current = controller;

    const nextPage = reset ? 1 : productsPage + 1;
    setLoadingProducts(true);
    try {
      const params = { page: nextPage, limit: PRODUCTS_PAGE_SIZE };
      if (categoryFilter) params.category = categoryFilter;
      if (debouncedSearch) params.search = debouncedSearch;

      const res = await axios.get(`${API_URL}/api/pos/products`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
        signal: controller.signal
      });
      const { rows, page, total, hasMore } = res.data;
      setProducts(prev => (reset ? rows : [...prev, ...rows]));
      setProductsPage(page);
      setProductsTotal(total);
      setProductsHasMore(hasMore);
    } catch (e) {
      if (axios.isCancel(e) || e.code === 'ERR_CANCELED') return;
      console.error('Error productos:', e);
    } finally {
      setLoadingProducts(false);
    }
  };

  const loadCategories = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/pos/categories`, { headers: { Authorization: `Bearer ${token}` } });
      setCategories(res.data);
    } catch (e) { console.error('Error categorías:', e); }
  };

  const loadPlanLimits = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/client/plan-limits/${clientData.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlanLimits(res.data);
    } catch (e) { console.error('Error plan limits:', e); }
  };

  const handleChange = (field, value) => {
    setNewProduct(prev => ({ ...prev, [field]: value }));
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showAlert('warning', 'Archivo inválido', 'Por favor selecciona una imagen (JPG, PNG, WEBP, etc.)');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      showAlert('warning', 'Imagen muy grande', 'La imagen no debe superar los 5MB.');
      return;
    }

    setUploadLoading(true);
    try {
      const base64 = await compressImage(file, 400, 0.75);
      setImagePreview(base64);
      setNewProduct(prev => ({ ...prev, image_url: base64 }));
    } catch (err) {
      showAlert('error', 'Error', 'No se pudo procesar la imagen.');
    } finally {
      setUploadLoading(false);
    }
  };

  const handleUrlChange = (value) => {
    handleChange('image_url', value);
    setImagePreview(value);
  };

  const clearImage = () => {
    setImagePreview('');
    setNewProduct(prev => ({ ...prev, image_url: '' }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleCreate = async () => {
    if (!newProduct.name || !newProduct.category || !newProduct.price) {
      showAlert('warning', 'Campos requeridos', 'Nombre, categoría y precio son obligatorios.');
      return;
    }
    try {
      const created = await axios.post(`${API_URL}/api/pos/products`, newProduct, { headers: { Authorization: `Bearer ${token}` } });
      // El vínculo con los grupos de opciones se guarda DESPUÉS de crear el
      // producto porque necesita su id. Si falla, el producto ya existe: se
      // avisa pero no se pierde el alta.
      const newId = created.data?.id;
      if (newId && productOptionGroups?.length) {
        try { await saveProductOptions(newId, productOptionGroups, token); }
        catch (_) { showAlert('warning', 'Producto creado', 'El producto se guardó, pero sus opciones no. Edítalo para reintentar.'); }
      }
      loadProducts({ reset: true }); loadPlanLimits(); closeModal();
      showAlert('success', 'Producto creado', 'El producto se agregó correctamente.');
    } catch (error) {
      if (error.response?.status === 403) {
        const d = error.response.data;
        showAlert('error', 'Límite alcanzado', `${d.error}\n\nPlan: ${planLimits?.limits?.name || 'Básico'}\nProductos: ${d.current}/${d.limit}\n\nActualiza tu plan para agregar más.`);
      } else {
        showAlert('error', 'Error', error.response?.data?.error || 'No se pudo crear el producto.');
      }
    }
  };

  const handleUpdate = async () => {
    try {
      await axios.put(`${API_URL}/api/pos/products/${editingProduct.id}`, newProduct, { headers: { Authorization: `Bearer ${token}` } });
      if (productOptionGroups !== null) {
        try { await saveProductOptions(editingProduct.id, productOptionGroups, token); }
        catch (_) { showAlert('warning', 'Guardado parcial', 'El producto se actualizó, pero sus opciones no.'); }
      }
      loadProducts({ reset: true }); closeModal();
      showAlert('success', 'Producto actualizado', 'Los cambios se guardaron correctamente.');
    } catch (e) {
      showAlert('error', 'Error', 'No se pudo actualizar el producto.');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/pos/products/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      loadProducts({ reset: true }); loadPlanLimits(); setConfirmDelete(null);
    } catch (e) {
      showAlert('error', 'Error', 'No se pudo eliminar el producto.');
    }
  };

  // ⭐ El listado paginado solo trae los campos que Caja necesita (no
  // incluye track_batches) — al editar se pide el detalle completo y
  // fresco por id, en vez de confiar en la fila ya cargada en la tabla.
  const openEditModal = async (product) => {
    setEditLoadingId(product.id);
    try {
      const res = await axios.get(`${API_URL}/api/pos/products/${product.id}`, { headers: { Authorization: `Bearer ${token}` } });
      const full = res.data;
      setEditingProduct(full);
      const imgUrl = full.image_url || '';
      const isBase64 = imgUrl.startsWith('data:');
      setImageMode(isBase64 ? 'upload' : 'url');
      setNewProduct({
        name: full.name,
        category: full.category,
        price: full.price,
        description: full.description || '',
        image_url: imgUrl,
        takeaway_surcharge: (full.takeaway_surcharge != null && full.takeaway_surcharge !== 0) ? full.takeaway_surcharge : '',
        stock: full.stock,
        use_stock: full.use_stock === 1,
        barcode: full.barcode || '',
        track_batches: full.track_batches === 1
      });
      setImagePreview(imgUrl);
      setShowModal(true);
    } catch (e) {
      showAlert('error', 'Error', 'No se pudo cargar el detalle del producto.');
    } finally {
      setEditLoadingId(null);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingProduct(null);
    // Vuelve a null (no []) para que el Linker sepa que tiene que volver a
    // sembrar desde el servidor la próxima vez que se abra un producto.
    setProductOptionGroups(null);
    setNewProduct(EMPTY_PRODUCT);
    setImagePreview('');
    setImageMode('upload');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ── Importación masiva ──
  const downloadImportTemplate = () => {
    const header = 'nombre,categoria,precio,stock,control_stock,codigo_barras,descripcion,recargo_para_llevar';
    const example = 'Hamburguesa Clásica,Comidas,4.50,20,si,7501234567890,Con queso y tocineta,0.50';
    downloadTextFile('plantilla_productos.csv', `${header}\n${example}\n`);
  };

  const openImportModal = () => {
    setImportStep('select');
    setImportRows([]);
    setImportPreview(null);
    setImportResult(null);
    setImportFileName('');
    setShowImportModal(true);
  };

  const closeImportModal = () => {
    setShowImportModal(false);
    if (importFileInputRef.current) importFileInputRef.current.value = '';
  };

  const handleImportFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImportFileName(file.name);
    setImportLoading(true);
    try {
      let rawObjects = [];
      if (/\.xlsx$/i.test(file.name)) {
        const buffer = await file.arrayBuffer();
        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(buffer);
        const worksheet = workbook.worksheets[0];
        const headerValues = worksheet.getRow(1).values || [];
        const headers = headerValues.slice(1).map((h) => (h != null ? h.toString() : ''));
        worksheet.eachRow((row, rowNumber) => {
          if (rowNumber === 1) return;
          const vals = (row.values || []).slice(1);
          const obj = {};
          headers.forEach((h, idx) => { obj[h] = vals[idx] != null ? vals[idx] : ''; });
          rawObjects.push(obj);
        });
      } else {
        const text = await file.text();
        rawObjects = csvTextToRawObjects(text);
      }

      const normalized = rawObjectsToImportRows(rawObjects);
      if (normalized.length === 0) {
        showAlert('warning', 'Archivo vacío', 'No se encontraron filas con datos. Verifica que el archivo tenga encabezados como en la plantilla.');
        return;
      }
      if (normalized.length > IMPORT_MAX_ROWS) {
        showAlert('warning', 'Archivo muy grande', `Máximo ${IMPORT_MAX_ROWS} productos por archivo. Divide el archivo en partes más pequeñas.`);
        return;
      }

      setImportRows(normalized);
      const res = await axios.post(`${API_URL}/api/pos/products/import/preview`, { rows: normalized }, { headers: { Authorization: `Bearer ${token}` } });
      setImportPreview(res.data);
      setImportStep('preview');
    } catch (err) {
      showAlert('error', 'Error al leer el archivo', err.response?.data?.error || 'Verifica que el archivo tenga el formato de la plantilla (CSV o XLSX).');
    } finally {
      setImportLoading(false);
    }
  };

  const handleConfirmImport = async () => {
    setImportLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/pos/products/import/commit`, { rows: importRows }, { headers: { Authorization: `Bearer ${token}` } });
      setImportResult(res.data);
      setImportStep('result');
      loadProducts({ reset: true });
      loadPlanLimits();
    } catch (err) {
      showAlert('error', 'Error al importar', err.response?.data?.error || 'No se pudo completar la importación.');
    } finally {
      setImportLoading(false);
    }
  };

  const downloadImportErrors = () => {
    if (!importResult?.errores?.length) return;
    const header = 'fila,nombre,motivo';
    const lines = importResult.errores.map((e) => `${e.fila},"${(e.nombre || '').replace(/"/g, '""')}","${e.motivo.replace(/"/g, '""')}"`);
    downloadTextFile('errores_importacion.csv', `${header}\n${lines.join('\n')}\n`);
  };

  const getPlanLimitInfo = () => {
    if (!planLimits) return null;
    const { limits } = planLimits;
    // ⭐ Total real del servidor, no products.length — con paginación, lo
    // cargado en pantalla es solo un pedazo del catálogo.
    const count = productsTotal;
    const max = limits.maxProducts;
    if (max === -1) return { text: `${count} productos (Ilimitado)`, pct: 0, color: 'text-green-600', bg: 'bg-green-100' };
    const pct = (count / max) * 100;
    let color = 'text-green-600', bg = 'bg-green-100';
    if (pct >= 90) { color = 'text-red-600'; bg = 'bg-red-100'; }
    else if (pct >= 70) { color = 'text-yellow-600'; bg = 'bg-yellow-100'; }
    return { text: `${count}/${max} productos`, pct, color, bg };
  };

  const limitInfo = getPlanLimitInfo();

  return (
    <div className="p-6">
      {customAlert && <CustomAlert {...customAlert} onClose={() => setCustomAlert(null)} compact zIndex={60} />}

      {/* ⭐ Pestañas de Inventario (2026-08-09): Productos y el catálogo de
          Opciones. Van en el mismo módulo porque configurar un plato con
          opciones obliga a saltar entre las dos todo el tiempo. */}
      <div className="flex gap-1 mb-4 border-b">
        {[['productos', 'Productos'], ['opciones', 'Opciones de platos']].map(([k, label]) => (
          <button key={k} onClick={() => setInventarioTab(k)}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition ${inventarioTab === k ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            {label}
          </button>
        ))}
      </div>

      {inventarioTab === 'opciones' && <OpcionesManager token={token} />}
      {inventarioTab === 'productos' && (
      <>

      {/* Confirm delete */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Eliminar producto</h3>
            <p className="text-gray-600 text-sm mb-6">¿Eliminar <strong>{confirmDelete.name}</strong>? Esta acción no se puede deshacer.</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmDelete(null)} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">Cancelar</button>
              <button onClick={() => handleDelete(confirmDelete.id)} className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">Eliminar</button>
            </div>
          </div>
        </div>
      )}

      {showLotesModal && editingProduct && (
        <GestionarLotesModal
          token={token}
          product={editingProduct}
          onClose={() => setShowLotesModal(false)}
          onStockChange={(newStock) => {
            setNewProduct(prev => ({ ...prev, stock: newStock }));
            loadProducts({ reset: true });
          }}
        />
      )}

      <ProductosPorVencerPanel token={token} />

      {/* Header */}
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Gestión de Productos</h2>
          {planLimits && limitInfo && (
            <div className="flex items-center gap-2 mt-2">
              <span className={`text-sm font-semibold ${limitInfo.color}`}>{limitInfo.text}</span>
              {planLimits.limits.maxProducts !== -1 && (
                <div className="w-32"><div className="w-full bg-gray-200 rounded-full h-2"><div className={`${limitInfo.bg} h-2 rounded-full`} style={{ width: `${Math.min(limitInfo.pct, 100)}%` }} /></div></div>
              )}
              <span className={`text-xs px-2 py-1 rounded ${limitInfo.bg} ${limitInfo.color} font-semibold`}>Plan {planLimits.limits.name}</span>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={exportProductsToXLSX}
            disabled={exportingProducts}
            className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 flex items-center gap-2 disabled:opacity-60"
          >
            <FileSpreadsheet className="w-5 h-5" /> {exportingProducts ? 'Exportando...' : 'Exportar'}
          </button>
          <button onClick={openImportModal} className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 flex items-center gap-2">
            <Upload className="w-5 h-5" /> Importar
          </button>
          <button onClick={() => setShowModal(true)} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2">
            <Plus className="w-5 h-5" /> Nuevo Producto
          </button>
        </div>
      </div>

      {planLimits && limitInfo && limitInfo.pct >= 80 && planLimits.limits.maxProducts !== -1 && (
        <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-yellow-800">Cerca del límite: <strong>{planLimits.limits.maxProducts}</strong> productos máx. en plan {planLimits.limits.name}.</p>
        </div>
      )}

      {/* Filtros */}
      <div className="mb-4 bg-white rounded-lg shadow p-4 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs font-medium text-gray-500 mb-1">Buscar</label>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Nombre o código de barras..."
            className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Categoría</label>
          <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">Todas</option>
            {categories.map((cat) => <option key={cat.id} value={cat.name}>{cat.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Precio</label>
          <select value={priceFilter} onChange={(e) => setPriceFilter(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">Todos</option>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
              <option key={n} value={n}>Hasta ${n}</option>
            ))}
            <option value="20+">Más de $20</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Stock</label>
          <select value={stockFilter} onChange={(e) => setStockFilter(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">Todos</option>
            <option value="lt10">Menos de 10</option>
            <option value="gt10">Más de 10</option>
            <option value="gt20">Más de 20</option>
          </select>
        </div>
        {hasActiveFilters && (
          <button onClick={clearFilters} className="text-sm text-blue-600 hover:underline pb-2">
            Limpiar filtros
          </button>
        )}
        <span className="text-xs text-gray-400 pb-2 ml-auto">
          {filteredProducts.length} de {productsTotal} productos
        </span>
      </div>
      {(priceFilter || stockFilter) && (
        <p className="text-xs text-gray-400 -mt-2 mb-4">
          Los filtros de precio y stock aplican solo sobre los productos ya cargados en pantalla — usa "Cargar más" para revisar el resto del catálogo.
        </p>
      )}

      {/* Tabla */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Producto</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Categoría</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Precio</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Arancel llevar</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stock</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredProducts.map((product) => (
              <tr key={product.id} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    {product.image_url ? (
                      <img src={product.image_url} alt={product.name}
                        className="w-10 h-10 rounded-lg object-cover border border-gray-200 flex-shrink-0"
                        onError={(e) => { e.target.style.display = 'none'; }} />
                    ) : (
                      <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                        <Package className="w-5 h-5 text-gray-400" />
                      </div>
                    )}
                    <div>
                      <p className="font-medium text-gray-900">{product.name}</p>
                      {product.description && <p className="text-xs text-gray-500 truncate max-w-[180px]">{product.description}</p>}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">{product.category}</td>
                <td className="px-4 py-3 text-sm font-semibold text-gray-900">${parseFloat(product.price).toFixed(2)}</td>
                <td className="px-4 py-3 text-sm">
                  {product.takeaway_surcharge > 0
                    ? <span className="text-orange-600 font-medium">+${parseFloat(product.takeaway_surcharge).toFixed(2)}</span>
                    : <span className="text-gray-400">—</span>}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">
                  {product.use_stock
                    ? <span className={product.stock <= 5 ? 'text-red-600 font-semibold' : ''}>{product.stock} unidades</span>
                    : <span className="text-gray-400">Sin control</span>}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-2">
                    <button onClick={() => openEditModal(product)} disabled={editLoadingId === product.id}
                      className="text-blue-600 hover:text-blue-900 p-1 disabled:opacity-40"><Edit2 className="w-4 h-4" /></button>
                    <button onClick={() => setConfirmDelete(product)} className="text-red-600 hover:text-red-900 p-1"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {loadingProducts && products.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">Cargando productos...</p>
          </div>
        )}
        {!loadingProducts && products.length === 0 && !hasActiveFilters && !debouncedSearch && (
          <div className="text-center py-12">
            <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No hay productos registrados</p>
            <button onClick={() => setShowModal(true)} className="mt-4 text-blue-600 hover:underline">Crear primer producto</button>
          </div>
        )}
        {!loadingProducts && products.length === 0 && (hasActiveFilters || debouncedSearch) && (
          <div className="text-center py-12">
            <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">Ningún producto coincide con la búsqueda/filtros</p>
            <button onClick={() => { clearFilters(); setSearchTerm(''); }} className="mt-4 text-blue-600 hover:underline">Limpiar filtros</button>
          </div>
        )}
        {products.length > 0 && filteredProducts.length === 0 && (
          <div className="text-center py-8 border-t">
            <p className="text-gray-500 text-sm">Ningún producto de esta página coincide con precio/stock</p>
            <button onClick={clearFilters} className="mt-2 text-blue-600 hover:underline text-sm">Limpiar filtros</button>
          </div>
        )}
        {productsHasMore && (
          <div className="text-center py-4 border-t">
            <button
              onClick={() => loadProducts({ reset: false })}
              disabled={loadingProducts}
              className="px-4 py-2 text-sm text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 disabled:opacity-50"
            >
              {loadingProducts ? 'Cargando...' : `Cargar más (${products.length} de ${productsTotal})`}
            </button>
          </div>
        )}
      </div>

      {/* Modal crear/editar */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[92vh] flex flex-col">
            {/* Header fijo */}
            <div className="px-6 pt-6 pb-4 border-b border-gray-100 flex-shrink-0">
              <h3 className="text-xl font-bold text-gray-900">
                {editingProduct ? 'Editar Producto' : 'Nuevo Producto'}
              </h3>
            </div>

            {/* Contenido scrollable */}
            <div className="overflow-y-auto flex-1 px-6 py-4 space-y-4">

              {/* Nombre */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
                <input type="text" value={newProduct.name} onChange={(e) => handleChange('name', e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ej: Hamburguesa Clásica" />
              </div>

              {/* Categoría */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Categoría *</label>
                <select value={newProduct.category} onChange={(e) => handleChange('category', e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">Seleccionar categoría</option>
                  {categories.map((cat) => <option key={cat.id} value={cat.name}>{cat.name}</option>)}
                </select>
              </div>

              {/* Descripción */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
                <textarea value={newProduct.description} onChange={(e) => handleChange('description', e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  placeholder="Descripción breve del producto..." />
              </div>

              {/* Precio + Arancel */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Precio *</label>
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-gray-500 text-sm">$</span>
                    <input type="number" step="0.01" min="0" value={newProduct.price}
                      onChange={(e) => handleChange('price', e.target.value)}
                      className="w-full pl-7 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="0.00" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Arancel Para Llevar
                    <span className="ml-1 text-xs text-gray-400">(opcional)</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-gray-500 text-sm">$</span>
                    <input type="number" step="0.01" min="0" value={newProduct.takeaway_surcharge}
                      onChange={(e) => handleChange('takeaway_surcharge', e.target.value)}
                      className="w-full pl-7 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="0.00" />
                  </div>
                  <p className="text-xs text-gray-400 mt-1">Se suma cuando es para llevar</p>
                </div>
              </div>

              {/* Imagen */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Imagen del Producto</label>

                {/* Toggle upload/URL */}
                <div className="flex rounded-lg border border-gray-200 overflow-hidden mb-3">
                  <button
                    type="button"
                    onClick={() => { setImageMode('upload'); clearImage(); }}
                    className={`flex-1 py-2 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${imageMode === 'upload' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                  >
                    <Upload className="w-4 h-4" />
                    Subir archivo
                  </button>
                  <button
                    type="button"
                    onClick={() => { setImageMode('url'); clearImage(); }}
                    className={`flex-1 py-2 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${imageMode === 'url' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                  >
                    <Link className="w-4 h-4" />
                    URL de imagen
                  </button>
                </div>

                {imageMode === 'upload' ? (
                  <div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="hidden"
                      id="product-image-input"
                    />
                    <label
                      htmlFor="product-image-input"
                      className={`flex flex-col items-center justify-center w-full h-28 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${uploadLoading ? 'border-blue-300 bg-blue-50' : 'border-gray-300 bg-gray-50 hover:bg-gray-100 hover:border-gray-400'}`}
                    >
                      {uploadLoading ? (
                        <p className="text-sm text-blue-600">Procesando imagen...</p>
                      ) : imagePreview ? (
                        <img src={imagePreview} alt="preview" className="h-24 object-contain rounded" />
                      ) : (
                        <>
                          <Upload className="w-8 h-8 text-gray-400 mb-2" />
                          <p className="text-sm text-gray-500">Haz clic para seleccionar una imagen</p>
                          <p className="text-xs text-gray-400 mt-1">JPG, PNG, WEBP — máx. 5MB</p>
                        </>
                      )}
                    </label>
                    {imagePreview && !uploadLoading && (
                      <button type="button" onClick={clearImage}
                        className="mt-2 flex items-center gap-1 text-xs text-red-500 hover:text-red-700">
                        <X className="w-3 h-3" /> Quitar imagen
                      </button>
                    )}
                  </div>
                ) : (
                  <div>
                    <input
                      type="url"
                      value={newProduct.image_url}
                      onChange={(e) => handleUrlChange(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="https://ejemplo.com/imagen.jpg"
                    />
                    {imagePreview && (
                      <div className="mt-2 flex items-center gap-3">
                        <img src={imagePreview} alt="preview"
                          className="w-16 h-16 rounded-lg object-cover border border-gray-200"
                          onError={(e) => { e.target.style.display = 'none'; }} />
                        <button type="button" onClick={clearImage}
                          className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700">
                          <X className="w-3 h-3" /> Quitar imagen
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Stock */}
              <div className="border-t pt-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={newProduct.use_stock}
                    onChange={(e) => handleChange('use_stock', e.target.checked)}
                    className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-medium text-gray-700">Controlar inventario</span>
                </label>
                {newProduct.use_stock && (
                  <div className="mt-3 space-y-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={newProduct.track_batches}
                        onChange={(e) => handleChange('track_batches', e.target.checked)}
                        className="w-4 h-4 text-blue-600" />
                      <span className="text-sm font-medium text-gray-700">Manejar por lotes con fecha de caducidad (PEPS/FIFO)</span>
                    </label>

                    {/* ⭐ Opciones de este plato (2026-08-09) */}
                    <div className="border-t pt-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Opciones del plato</label>
                      <ProductOptionsLinker
                        productId={editingProduct?.id}
                        token={token}
                        value={productOptionGroups}
                        onChange={setProductOptionGroups}
                      />
                    </div>

                    {newProduct.track_batches ? (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                        <p className="text-blue-800">
                          Stock actual (suma de lotes activos): <span className="font-bold">{newProduct.stock}</span>
                        </p>
                        {editingProduct ? (
                          <button type="button" onClick={() => setShowLotesModal(true)}
                            className="mt-2 w-full py-2 bg-blue-600 text-white rounded-lg font-semibold text-sm hover:bg-blue-700">
                            Gestionar Lotes
                          </button>
                        ) : (
                          <p className="text-xs text-blue-600 mt-1">Guarda el producto primero; después podrás registrar sus lotes desde "Gestionar Lotes".</p>
                        )}
                      </div>
                    ) : (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Stock</label>
                        <input type="number" min="0" value={newProduct.stock}
                          onChange={(e) => handleChange('stock', parseInt(e.target.value) || 0)}
                          className="w-32 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      </div>
                    )}
                  </div>
                )}

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Código de barras (opcional)</label>
                  <input type="text" value={newProduct.barcode}
                    onChange={(e) => handleChange('barcode', e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Escanea o escribe el código" />
                </div>
              </div>
            </div>

            {/* Footer fijo */}
            <div className="px-6 pb-6 pt-4 border-t border-gray-100 flex-shrink-0 flex gap-3">
              <button onClick={closeModal} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">
                Cancelar
              </button>
              <button onClick={editingProduct ? handleUpdate : handleCreate}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">
                {editingProduct ? 'Guardar Cambios' : 'Crear Producto'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de importación masiva */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col">
            <div className="px-6 pt-6 pb-4 border-b border-gray-100 flex-shrink-0 flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900">Importar productos</h3>
              <button onClick={closeImportModal} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="overflow-y-auto flex-1 px-6 py-4">
              {importStep === 'select' && (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">
                    Sube un archivo CSV o Excel (.xlsx) con tus productos. Las categorías deben existir antes de importar —
                    créalas primero si hace falta.
                  </p>
                  <button onClick={downloadImportTemplate} className="flex items-center gap-2 text-sm text-blue-600 hover:underline">
                    <Download className="w-4 h-4" /> Descargar plantilla de ejemplo
                  </button>
                  <input
                    ref={importFileInputRef}
                    type="file"
                    accept=".csv,.xlsx"
                    onChange={handleImportFile}
                    className="hidden"
                    id="import-file-input"
                  />
                  <label
                    htmlFor="import-file-input"
                    className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${importLoading ? 'border-blue-300 bg-blue-50' : 'border-gray-300 bg-gray-50 hover:bg-gray-100 hover:border-gray-400'}`}
                  >
                    {importLoading ? (
                      <p className="text-sm text-blue-600">Procesando archivo...</p>
                    ) : (
                      <>
                        <FileSpreadsheet className="w-8 h-8 text-gray-400 mb-2" />
                        <p className="text-sm text-gray-500">{importFileName || 'Haz clic para seleccionar un archivo'}</p>
                        <p className="text-xs text-gray-400 mt-1">CSV o XLSX — máx. {IMPORT_MAX_ROWS.toLocaleString()} filas</p>
                      </>
                    )}
                  </label>
                </div>
              )}

              {importStep === 'preview' && importPreview && (
                <div className="space-y-4">
                  <div className="flex flex-wrap gap-3 text-sm items-center">
                    <span className="px-3 py-1 rounded-full bg-green-100 text-green-700 font-semibold">{importPreview.resumen.crear} a crear</span>
                    <span className="px-3 py-1 rounded-full bg-blue-100 text-blue-700 font-semibold">{importPreview.resumen.actualizar} a actualizar</span>
                    <span className="px-3 py-1 rounded-full bg-red-100 text-red-700 font-semibold">{importPreview.resumen.errores} con error</span>
                    <span className="text-gray-400 ml-auto">{importPreview.total} filas totales</span>
                  </div>

                  <div className="border rounded-lg overflow-hidden overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Fila</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Categoría</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Precio</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {importPreview.preview.map((f) => (
                          <tr key={f.index}>
                            <td className="px-3 py-2 text-gray-500">{f.index + 1}</td>
                            <td className="px-3 py-2">{f.name || <span className="text-gray-400">—</span>}</td>
                            <td className="px-3 py-2">{f.category || <span className="text-gray-400">—</span>}</td>
                            <td className="px-3 py-2">{f.price ? `$${Number(f.price).toFixed(2)}` : '—'}</td>
                            <td className="px-3 py-2">
                              {f.accion === 'crear' && <span className="text-green-600 font-medium">Crear</span>}
                              {f.accion === 'actualizar' && <span className="text-blue-600 font-medium">Actualizar</span>}
                              {f.accion === 'error' && (
                                <span className="text-red-600 font-medium">Error: {f.errors.join('; ')}</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {importPreview.total > 20 && (
                    <p className="text-xs text-gray-400">Mostrando las primeras 20 filas de {importPreview.total}.</p>
                  )}
                  {importPreview.resumen.errores > 0 && (
                    <p className="text-xs text-orange-600">Las filas con error se omitirán; el resto se importará normalmente.</p>
                  )}
                </div>
              )}

              {importStep === 'result' && importResult && (
                <div className="space-y-4">
                  <div className="flex flex-wrap gap-3 text-sm">
                    <span className="px-3 py-1 rounded-full bg-green-100 text-green-700 font-semibold">{importResult.creados} creados</span>
                    <span className="px-3 py-1 rounded-full bg-blue-100 text-blue-700 font-semibold">{importResult.actualizados} actualizados</span>
                    <span className="px-3 py-1 rounded-full bg-red-100 text-red-700 font-semibold">{importResult.omitidos} omitidos</span>
                  </div>
                  {importResult.errores?.length > 0 && (
                    <div>
                      <div className="max-h-48 overflow-y-auto border rounded-lg divide-y divide-gray-100 text-sm">
                        {importResult.errores.slice(0, 50).map((e, idx) => (
                          <div key={idx} className="px-3 py-2">
                            <span className="font-medium">Fila {e.fila}</span> — {e.nombre}: <span className="text-red-600">{e.motivo}</span>
                          </div>
                        ))}
                      </div>
                      <button onClick={downloadImportErrors} className="mt-2 flex items-center gap-1 text-sm text-blue-600 hover:underline">
                        <Download className="w-4 h-4" /> Descargar lista de errores
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="px-6 pb-6 pt-4 border-t border-gray-100 flex-shrink-0 flex gap-3">
              {importStep === 'select' && (
                <button onClick={closeImportModal} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">
                  Cancelar
                </button>
              )}
              {importStep === 'preview' && (
                <>
                  <button onClick={() => setImportStep('select')} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">
                    Atrás
                  </button>
                  <button
                    onClick={handleConfirmImport}
                    disabled={importLoading || importPreview.resumen.crear + importPreview.resumen.actualizar === 0}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50"
                  >
                    {importLoading ? 'Importando...' : `Confirmar importación (${importPreview.resumen.crear + importPreview.resumen.actualizar})`}
                  </button>
                </>
              )}
              {importStep === 'result' && (
                <button onClick={closeImportModal} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">
                  Cerrar
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}

export default ProductosManager;
